/**
 * Site dashboard view — operational main stage for a single site.
 *
 * Rendered as the index child of SiteDashboardLayout. The parent layout owns
 * the shared rail, summary, dashboard data subscription, and command blade;
 * this route owns the dashboard-specific sections and blades.
 *
 * Contains:
 * - SiteDashboardView — route child component (default export)
 */
import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import { Box, Button } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useUser } from '@sophocles/app';
import { selectSiteFeatureAssociationByKey } from '@sophocles/features/entities/site';
import { selectDisableThirdPartyApi } from '@sophocles/features/tenant/slice';
import { userPermCheck } from '@sophocles/utils';
import CameraDialog from '@sophocles/views/sites/components/CameraCards/CameraDialog';
import CameraCard, { ULTRAWIDE_ASPECT_RATIO } from '@sophocles/views/sites/components/SiteDashboard/CameraCard';
import CameraSchedulesBlade from '@sophocles/views/sites/components/SiteDashboard/CameraSchedulesBlade';
import DashboardCard from '@sophocles/views/sites/components/SiteDashboard/DashboardCard';
import DashboardCardSkeleton from '@sophocles/views/sites/components/SiteDashboard/DashboardCardSkeleton';
import {
  filterDashboardSensors,
  isVantageUnit,
  shouldShowSensorCard,
} from '@sophocles/views/sites/components/SiteDashboard/dashboardDerivations';
import DashboardSection from '@sophocles/views/sites/components/SiteDashboard/DashboardSection';
import IntrusionBlade from '@sophocles/views/sites/components/SiteDashboard/IntrusionBlade';
import IntrusionSummaryCard from '@sophocles/views/sites/components/SiteDashboard/IntrusionSummaryCard';
import MaintenanceBlade from '@sophocles/views/sites/components/SiteDashboard/MaintenanceBlade';
import RelayBlade from '@sophocles/views/sites/components/SiteDashboard/RelayBlade';
import RelaySection from '@sophocles/views/sites/components/SiteDashboard/RelaySection';
import SensorBlade from '@sophocles/views/sites/components/SiteDashboard/SensorBlade';
import SensorSummaryCard from '@sophocles/views/sites/components/SiteDashboard/SensorSummaryCard';
import { useSiteDashboardLayoutContext } from '@sophocles/views/sites/SiteDashboardLayout';

// Default relay count shown while unit data is loading.
const DEFAULT_SKELETON_RELAY_COUNT = 4;

function SiteDashboardInitialSkeleton() {
  return (
    <>
      {/* Relay section — the same unit grid the real RelaySection lays out on */}
      <DashboardSection>
        {Array.from({ length: DEFAULT_SKELETON_RELAY_COUNT }, (_, i) => (
          <DashboardCard key={`sk-relay-${i}`} span={1}>
            <DashboardCardSkeleton variant="relay" />
          </DashboardCard>
        ))}
      </DashboardSection>
    </>
  );
}

function SiteDashboardView() {
  const disableThirdPartyApi = useSelector(selectDisableThirdPartyApi);

  const {
    params,
    siteId,
    customer,
    selectedStructure,
    selectedPlant,
    loading,
    unit,
    site,
    curfew,
    isBillboard,
    relays,
    addonCic,
    siteIccid,
    canManageUnit,
    canCaptureCamera,
    canEditCamera,
    canAddCameraSchedule,
    canSendCommand,
    commandLocked,
    canSendIntrusionCommands,
    canEditIntrusionSettings,
    canEditIntrusionInputs,
    canManageIntrusionConfiguration,
    canConfigureSensors,
    blades,
  } = useSiteDashboardLayoutContext();

  const hasCharting = customer.getIn(['options', 'charting_option'], 0) > 0 && !disableThirdPartyApi;

  const calendarFeature = useSelector(selectSiteFeatureAssociationByKey(siteId ?? 0, 'CALENDAR'));
  const usesCalendar = calendarFeature?.is_effectively_enabled ?? false;

  const {
    isRelayBladeOpen,
    relayBladeParams,
    openRelayBlade,
    closeRelayBlade,
    maintenanceOpen,
    maintenanceAddToken,
    maintenanceFocusedLogId,
    closeMaintenance,
    scheduleCameraId,
    openCameraSchedules,
    closeCameraSchedules,
    sensorConfigOpen,
    openSensorConfig,
    closeSensorConfig,
    intrusionConfigOpen,
    openIntrusionConfig,
    closeIntrusionConfig,
  } = blades;

  const showCameraSection = addonCic != null;

  const cameraEntries = useMemo(() => {
    if (!showCameraSection || !addonCic?.cameras) return [];
    return [...addonCic.cameras]
      .sort((a, b) => a.camera_number - b.camera_number)
      .map(camera => ({ camera, cic: addonCic }));
  }, [showCameraSection, addonCic]);

  const camerasById = useMemo(() => {
    const map = {};
    if (addonCic?.cameras) {
      addonCic.cameras.forEach(cam => {
        map[cam.id] = cam;
      });
    }
    return map;
  }, [addonCic]);
  const scheduleCamera = scheduleCameraId != null ? (camerasById[scheduleCameraId] ?? null) : null;

  const intrusion = unit?.intrusion ?? null;
  const intrusionConfiguration = unit?.intrusion_configuration ?? null;
  const intrusionConfigPermissions = useMemo(
    () => ({
      can_edit_settings: canEditIntrusionSettings,
      can_edit_inputs: canEditIntrusionInputs,
    }),
    [canEditIntrusionInputs, canEditIntrusionSettings],
  );
  const showIntrusionCard = intrusion != null;
  const intrusionVariant = intrusion?.variant ?? 'v1';

  const sensors = useMemo(() => filterDashboardSensors(unit?.sensors), [unit?.sensors]);
  const sensorConfiguration = unit?.sensor_configuration ?? null;
  const isVantage = useMemo(() => isVantageUnit(unit), [unit]);
  const showSensorCard = useMemo(() => shouldShowSensorCard(unit), [unit]);

  // Add-camera parity with the classic dashboard: real cameras only — a
  // Vantage unit's screenshot source is not user-addable. The CIC caps how
  // many cameras exist (cameras_installed) and creation takes the add_camera
  // permission. The button rides under the camera cards, so a camera-capable
  // CIC with no cameras yet still offers it.
  const { user } = useUser();
  const { t } = useTranslation();
  const [addCameraOpen, setAddCameraOpen] = useState(false);

  // Aspect ratio of each camera's latest capture, keyed by camera id, reported
  // by CameraCard once the image decodes.
  const [cameraAspectRatios, setCameraAspectRatios] = useState({});
  const handleCameraAspectRatio = useCallback((cameraId, aspectRatio) => {
    setCameraAspectRatios(prev => (prev[cameraId] === aspectRatio ? prev : { ...prev, [cameraId]: aspectRatio }));
  }, []);
  const canAddCamera =
    showCameraSection &&
    !isVantage &&
    userPermCheck(user, 'pliny.add_camera') &&
    cameraEntries.length < (addonCic?.cameras_installed ?? 0);

  const showSystemCards = cameraEntries.length > 0 || canAddCamera || showIntrusionCard;

  const showInitialSkeleton = loading && unit == null;

  if (showInitialSkeleton) {
    return <SiteDashboardInitialSkeleton />;
  }

  return (
    <>
      <RelaySection
        loading={loading}
        siteId={siteId}
        unit={unit}
        canManageUnit={canManageUnit}
        siteTimeZone={site?.time_zone ?? 'UTC'}
        customer={customer}
        params={params}
        relays={relays}
        usesCalendar={usesCalendar}
        isBillboard={isBillboard}
        hasCharting={hasCharting}
        curfew={curfew}
        siteIccid={siteIccid}
        nextSunriseRaw={site?.next_sunrise ?? null}
        nextSunsetRaw={site?.next_sunset ?? null}
        onClickEditSchedule={(relayId, scheduleId, initialTab = 'config') => {
          openRelayBlade(relayId, scheduleId, initialTab);
        }}
        trailingContent={
          showSensorCard ? (
            <SensorSummaryCard
              sensors={sensors}
              onOpenSensorConfig={canConfigureSensors ? openSensorConfig : undefined}
            />
          ) : null
        }
      />

      {showSystemCards ? (
        <DashboardSection>
          {cameraEntries.map(({ camera, cic }) => {
            // A camera's footprint follows the shape of what it actually
            // captures: a panoramic frame earns the second unit, an ordinary
            // one would only letterbox inside it.
            const isUltrawide = cameraAspectRatios[camera.id] >= ULTRAWIDE_ASPECT_RATIO;
            return (
              <DashboardCard key={`camera-${camera.id}`} span={isUltrawide ? 2 : 1}>
                <CameraCard
                  site={selectedStructure}
                  cic={cic}
                  camera={camera}
                  params={params}
                  isVantage={isVantage}
                  isStandAlone={false}
                  onOpenSchedules={openCameraSchedules}
                  canCapture={canCaptureCamera}
                  canEdit={canEditCamera}
                  canAddSchedule={canAddCameraSchedule}
                  onAspectRatioChange={handleCameraAspectRatio}
                />
              </DashboardCard>
            );
          })}

          {canAddCamera && (
            // Full-width grid row directly beneath the camera cards (and above
            // the intrusion card), holding just the button.
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Button variant="outlined" startIcon={<AddAPhotoOutlinedIcon />} onClick={() => setAddCameraOpen(true)}>
                {t('sites.addCamera', 'Add Camera')}
              </Button>
              {addCameraOpen && (
                <CameraDialog
                  open
                  onClose={() => setAddCameraOpen(false)}
                  siteId={siteId}
                  camera={null}
                  canEdit
                  canDelete={false}
                  cic={addonCic}
                  cicHasRadio={false}
                  isStandAlone={false}
                  isVantage={isVantage}
                />
              )}
            </Box>
          )}

          {showIntrusionCard && (
            <DashboardCard span={1}>
              <IntrusionSummaryCard
                site={selectedStructure}
                variant={intrusionVariant}
                intrusion={intrusion}
                loading={loading}
                canSendCommands={canSendIntrusionCommands}
                commandLocked={commandLocked}
                canManageConfiguration={canManageIntrusionConfiguration}
                onOpenSettings={openIntrusionConfig}
              />
            </DashboardCard>
          )}
        </DashboardSection>
      ) : null}

      <RelayBlade
        customerId={params.cust_id}
        siteId={params.site_id}
        siteGroupId={params.sitegroup_id}
        open={isRelayBladeOpen}
        initialRelayId={relayBladeParams.initialRelayId}
        initialScheduleId={relayBladeParams.initialScheduleId}
        initialTab={relayBladeParams.initialTab}
        onClose={closeRelayBlade}
        siteData={site}
        isBillboardOverride={isBillboard}
      />

      {showSensorCard && (
        <SensorBlade
          open={sensorConfigOpen}
          onClose={closeSensorConfig}
          siteId={siteId}
          sensorConfiguration={sensorConfiguration}
          loading={loading}
          canSendCommands={canSendCommand}
        />
      )}

      {showIntrusionCard && (
        <IntrusionBlade
          open={intrusionConfigOpen}
          onClose={closeIntrusionConfig}
          siteId={siteId}
          intrusionConfiguration={intrusionConfiguration}
          permissions={intrusionConfigPermissions}
          loading={loading}
        />
      )}

      <MaintenanceBlade
        open={maintenanceOpen}
        onClose={closeMaintenance}
        siteId={siteId}
        maintenancePath={`/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}/maintenancehist`}
        startAddingToken={maintenanceAddToken}
        focusedLogId={maintenanceFocusedLogId}
      />

      <CameraSchedulesBlade
        siteId={siteId}
        open={Boolean(scheduleCamera)}
        onClose={closeCameraSchedules}
        camera={scheduleCamera}
        isVantage={isVantage}
        canAddSchedules={canAddCameraSchedule}
      />
    </>
  );
}

export default SiteDashboardView;
